import React, { useCallback, useEffect, useState, useMemo } from 'react';
import parties from '../data/party';
import categoriesApi from '../data/categories';
import stockes from '../data/stockhistory';
import stockdata from '../data/stock';
import { fetchStocknames } from '../data/Stocknames';
import { fetchMutualnames } from '../data/Mutual';
import Select from "react-select";
import { useRef } from "react";
import settingapi from '../data/setting';
// ...above, in your component function body (not inside useEffect):

// ... (interfaces/utility types unchanged) ...

interface Props {}

interface Party {
    id: number;
    date: string;
    name: string;
    number: string;
    contact_person_name: string;
    contact_person_number: string;
    categories: string[];
    deleteparty: number;
    total_amount?: number | string;
}

interface CategoryApiType {
    id: number | string;
    category_name: string;
}

interface Stock {
    id: number | string;
    name: string;
    category: string;
}

type StockCategoryInfo = {
    id: number | string;
    stockId: number | string;
    stockName: string;
    unit: string;
    totalAmount: string;
    indirectCharges: string;
    perUnitCharge: string;
    autoCalculated: boolean;
    category: string;
    error?: string;
};
interface StockNames {
    id: number;
    name: string;
    screener: string;
    ltp: string;
    change_percent: string;
    open_price: string;
    volume: number;
    market_cap_cr: string;
    pe_ratio: string;
    industry_pe: string;
    high_52w: string;
    low_52w: string;
    returns_1m: string;
    returns_3m: string;
    returns_1y: string;
    returns_3y: string;
    returns_5y: string;
    pb_ratio: string;
    dividend: string;
    roe: string;
    roce: string;
    eps: string;
    dma_50: string;
    dma_200: string;
    rsi: string;
    margin_funding: string;
    margin_pledge: string;
    [key: string]: any;
  }
type CategoryEntryMap = { [categoryName: string]: StockCategoryInfo[] };

type PartyStockDBType = {
    id: number | string;
    date:string;
    party_id: number | string;
    categories: string | (StockDBCategoryItem | DirectCategoryItem)[];
};

type StockDBCategoryItem = {
    id: number | string;
    investment_type: string;
    category: string;
    stock_name: string;
    unit: string;
    total_amount: string;
    indirect_charges: string;
    perUnitCharge: string;
};

type DirectCategoryItem = {
    investment_type: string;
    total_amount: string | number;
    date?: string;
    [key: string]: any;
};
const safeNumber = (val: any) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
};
let globalEntryId = 1;

async function fetchStocksAPI() {
    return [
        { id: 1, name: 'SBI', category: 'Equity' },
        { id: 2, name: 'HDFC', category: 'Equity' },
        { id: 3, name: 'RELIANCE', category: 'Equity' },
        { id: 4, name: 'NIFTY-FUT', category: 'FNO' },
        { id: 5, name: 'BANKNIFTY-OPT', category: 'FNO' },
        { id: 6, name: 'ICICI MF', category: 'Mutual Funds' },
        { id: 7, name: 'SBI MF', category: 'Mutual Funds' },
        { id: 8, name: 'NIFTY ETF', category: 'ETF' },
        { id: 9, name: 'GOLD ETF', category: 'ETF' },
        { id: 10, name: 'ETF Option A', category: 'ETFO' },
        { id: 11, name: 'ETF Option B', category: 'ETFO' },
    ];
}

function emptyStockCategoryInfo(category = '', stockId: number | string = '', stockName = ''): StockCategoryInfo {
    const id = globalEntryId++;
    return {
        id,
        stockId,
        stockName,
        category,
        unit: '',
        totalAmount: '',
        indirectCharges: '',
        perUnitCharge: '',
        autoCalculated: true,
        error: undefined
    };
}

function getAvailableUnit(
    categoryEntries: CategoryEntryMap,
    category: string,
    stockId: number | string
): number {
    if (!category || !stockId) return 0;
    const entries = (categoryEntries[category] || []).filter(
        entry => entry.stockId == stockId
    );
    let total = 0;
    for (const entry of entries) {
        const n = parseFloat(entry.unit);
        if (!isNaN(n)) total += n;
    }
    return total;
}

// Returns (sum of): category allocations (total_amount+indirect_charges), PLUS direct payments (total_amount where investment_type==='direct') for given party
function calculateTotalDeductedIncludingDirect(stockDbRows: PartyStockDBType[], partyId: string | number): number {
    let totalDeducted = 0;
    stockDbRows.forEach(row => {
        if (String(row.party_id) !== String(partyId)) return;
        let catsArr: (StockDBCategoryItem | DirectCategoryItem)[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed : [];
            } catch {
                catsArr = [];
            }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as (StockDBCategoryItem | DirectCategoryItem)[];
        }
        for (const item of catsArr) {
            if (
                typeof item === "object" &&
                (item as any).investment_type === "direct" &&
                (item as any).total_amount !== undefined
            ) {
                // Sum up direct investment_type total_amount for deduction
                let amt = parseFloat(String((item as any).total_amount) ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
            } else if (
                typeof item === "object" &&
                (item as any).category
            ) {
                let amt = parseFloat((item as StockDBCategoryItem).total_amount ?? "0");
                let indirect = parseFloat((item as StockDBCategoryItem).indirect_charges ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
                if (!isNaN(indirect)) totalDeducted += indirect;
            }
        }
    });
    return totalDeducted;
}

// Only returns sum of (total_amount+indirect_charges) for category allocations, NOT direct payments
function calculateTotalDeductedFromStockTable(stockDbRows: PartyStockDBType[], partyId: string | number): number {
    let totalDeducted = 0;
    stockDbRows.forEach(row => {
        if (String(row.party_id) !== String(partyId)) return;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed : [];
            } catch {
                catsArr = [];
            }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (typeof item === "object" && 'category' in item) {
                let amt = parseFloat(item.total_amount ?? "0");
                let indirect = parseFloat(item.indirect_charges ?? "0");
                if (!isNaN(amt)) totalDeducted += amt;
                if (!isNaN(indirect)) totalDeducted += indirect;
            }
        }
    });
    return totalDeducted;
}

// ---- Sell Stock Modal logic ----

type SellStockFormState = {
    partyId: string;
    category: string;
    stockId: string | number;
    stockName: string;
    sellUnits: string;
    sellAmount: string;
    indirectCharges: string;

    date: string;
};

function getStockUnitAndTotalAmount(stocksDbRows: PartyStockDBType[], partyId: string, category: string, stockId: string | number, stockName: string): {totalUnits: number, totalAmount: number} {
    let totalUnits = 0;
    let totalAmount = 0;
    for (const row of stocksDbRows) {
        if (String(row.party_id) !== String(partyId)) continue;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed as StockDBCategoryItem[] : [];
            } catch { catsArr = [] }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (
                item.category === category &&
                (String(item.id) === String(stockId) || String(item.stock_name) === String(stockName))
            ) {
                const u = parseFloat(item.unit);
                const a = parseFloat(item.total_amount);
                if (!isNaN(u)) totalUnits += u;
                if (!isNaN(a)) totalAmount += a;
            }
        }
    }
    return {totalUnits, totalAmount};
}

function getAvailableDBStocksForParty(stocksDbRows: PartyStockDBType[], partyId: string) {
    let out: { [category: string]: StockDBCategoryItem[] } = {};
    for (const row of stocksDbRows) {
        if (String(row.party_id) !== String(partyId)) continue;
        let catsArr: StockDBCategoryItem[] = [];
        if (typeof row.categories === 'string') {
            try {
                const parsed = JSON.parse(row.categories);
                catsArr = Array.isArray(parsed) ? parsed as StockDBCategoryItem[] : [];
            } catch { catsArr = [] }
        } else if (Array.isArray(row.categories)) {
            catsArr = row.categories as StockDBCategoryItem[];
        }
        for (const item of catsArr) {
            if (!out[item.category]) out[item.category] = [];
            out[item.category].push(item);
        }
    }
    return out;
}

function Addstock(props: Props) {
    // --- existing state and hooks ---
    const [data, setData] = useState({
        partyId: '',
        date: '',
    });

    const [investmentType, setInvestmentType] = useState<'direct' | 'structure'>('structure');
    const [directPaymentAmount, setDirectPaymentAmount] = useState('');
    const [hasDirectPending, setHasDirectPending] = useState(false);

    const [categoryEntries, setCategoryEntries] = useState<CategoryEntryMap>({});
    const [partiesList, setPartiesList] = useState<Party[]>([]);
    const [allStocks, setAllStocks] = useState<Stock[]>([]);
    const [categoriesFromApi, setCategoriesFromApi] = useState<CategoryApiType[]>([]);
    const [possibleStocksByCategory, setPossibleStocksByCategory] = useState<{ [cat: string]: Stock[] }>({});
    const [possibleCategories, setPossibleCategories] = useState<CategoryApiType[]>([]);
    const [stocksDb, setStocksDb] = useState<PartyStockDBType[]>([]);
    const [stocksDataDb, setStocksDataDb] = useState<PartyStockDBType[]>([]);
    const [withdrawCategoriesDb, setWithdrawCategoriesDb] = useState<string[]>([]);
    const [withdrawStocksDb, setWithdrawStocksDb] = useState<StockDBCategoryItem[]>([]);
    const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
    const [urlParty, setUrlParty] = useState<string>('');
    const[totalamountspend, setTotalAmmountSpend] = useState<Number>();

    const [stocksnames, setStocksnames] = useState<StockNames[]>([]);
    const [mutualsnames, setMutualsnames] = useState<StockNames[]>([]);
    const exceedPromptedRef = useRef(false);
    const lastCheckedDeductedRef = useRef<number | null>(null);
    const exceedPromptedOnceRef = useRef(false);
    const lastDeductedAboveRef = useRef<number | null>(null);
    const[farmName, setFarmName] = useState<string>();
    useEffect(() => {
        async function fetchSettingsApi() {
          try {
            const res = await settingapi();
            if (Array.isArray(res)) {
              const farmSetting = res.find(
                (item: any) => item.meta_key === "farm"
              );
              if (farmSetting && farmSetting.meta_value !== undefined) {
                let farmObj = {};
                try {
                  farmObj = typeof farmSetting.meta_value === "string"
                    ? JSON.parse(farmSetting.meta_value)
                    : farmSetting.meta_value;
                } catch {}
                if (
                  farmObj &&
                  typeof farmObj === "object" &&
                  farmObj !== null &&
                  "name" in farmObj &&
                  typeof (farmObj as { name?: unknown }).name === "string"
                ) {
                  setFarmName((farmObj as { name: string }).name);
                }
              }
            }
          } catch {}
        }
        fetchSettingsApi();
      }, []);
    // URL param logic etc. unchanged
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const partyId = urlParams.get('party') || '';
        setUrlParty(partyId);
    }, []);
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const partyId = urlParams.get('party') || '';
        setUrlParty(partyId);
        setData(prevData => {
            if (!prevData.partyId && partyId) {
                return { ...prevData, partyId };
            }
            return prevData;
        });
    }, []);

    const [withdrawalInvestmentType, setWithdrawalInvestmentType] = useState<'direct' | 'structure'>('structure');
    const [withdrawalDirectPayment, setWithdrawalDirectPayment] = useState('');

    const [withdrawal, setWithdrawal] = useState<{
        partyId: string;
        category: string;
        stockId: number | string;
        stockName: string;
        sale: string;
        date: string;
    }>({
        partyId: '',
        category: '',
        stockId: '',
        stockName: '',
        sale: '',
        date: '',
    });
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);

    const [selectedPartyTotalAmount, setSelectedPartyTotalAmount] = useState<number | null>(null);
    const [deductedAvailable, setDeductedAvailable] = useState<number | null>(null);

    const [entryErrors, setEntryErrors] = useState<{ [cat: string]: { [idx: number]: string } }>({});

    const [directToStructureMeta, setDirectToStructureMeta] = useState<{directPaymentAmount: string, investmentType: "direct", metaDate: string} | null>(null);

    // ----- MODAL for Sell Stock ----
    const [showSellStockModal, setShowSellStockModal] = useState(false);
    const [sellStockLoading, setSellStockLoading] = useState(false);
    const [sellStockForm, setSellStockForm] = useState<SellStockFormState>({
        partyId: '',
        category: '',
        stockId: '',
        stockName: '',
        sellUnits: '',
        sellAmount:'',
        indirectCharges:"",
        date: '',
    });
    const [sellStockFormAmount, setSellStockFormAmount] = useState<number>(0);

    // Fill categories/stocks that are available to the selected party
    const sellAvailableDBStocks = useMemo(() => getAvailableDBStocksForParty(stocksDataDb, sellStockForm.partyId), [stocksDataDb, sellStockForm.partyId]);
    const sellPartyCategories = useMemo(
        () => Object.keys(sellAvailableDBStocks), [sellAvailableDBStocks]
    );

    // Stocks in selected category for sellStock modal
    const sellCategoryStocks = useMemo(() => {
        if (!sellStockForm.category) return [];
        return (sellAvailableDBStocks[sellStockForm.category] || []);
    }, [sellStockForm.category, sellAvailableDBStocks]);

    const fetchUserRemainingBalance = useCallback(async (partyId: string) => {
        if (!partyId) {
          setTotalAmmountSpend(0);
          return;
        }
    
        try {
          const usermetaResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`);
          const usermetaList = await usermetaResponse.json();
          
          // Find the record where meta_key is "remaining" and party_id matches
          const foundMeta = usermetaList.find(
            (meta: any) => meta.meta_key === "remaining" && String(meta.party_id) === String(partyId)
          );
    
          if (foundMeta) {
            setTotalAmmountSpend(Number(foundMeta.meta_value));
          } else {
            // Fallback if no meta found: set to 0 or get original total_amount from partiesList
            const party = partiesList.find(p => String(p.id) === String(partyId));
            setTotalAmmountSpend(Number(party?.total_amount || 0));
          }
        } catch (err) {
          console.error("Error fetching usermeta:", err);
          setTotalAmmountSpend(0);
        }
      }, [partiesList]);

      useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const partyId = urlParams.get('party') || '';
        setUrlParty(partyId);
        setData(prevData => {
            if (!prevData.partyId && partyId) {
                // Fetch the balance for the URL party ID
                fetchUserRemainingBalance(partyId);
                return { ...prevData, partyId };
            }
            return prevData;
        });
    }, [fetchUserRemainingBalance]);

    
    // Get currently available units and totalAmount for computation
    const sellStockAvailableDetails = useMemo(() => {
        if (!sellStockForm.partyId || !sellStockForm.category || !sellStockForm.stockId) {
            return { unit: 0, amount: 0, current_price: 0 };
        }
        // Find matching DB item for party/category/stockId
        let foundItem = sellCategoryStocks.find(
            s =>
                String(s.id) === String(sellStockForm.stockId)
                || String(s.stock_name) === String(sellStockForm.stockName)
        );
        if (!foundItem) return { unit: 0, amount: 0, current_price: 0 };

        let nUnit = parseFloat(foundItem.unit || '0');
        let nPrice = parseFloat(foundItem.total_amount || '0'); // treating total_amount as current_price here

        return {
            unit: !isNaN(nUnit) ? nUnit : 0,
            amount: !isNaN(nPrice) ? nPrice : 0, // Keeping legacy "amount" for downstream usage
            current_price: !isNaN(nPrice) ? nPrice : 0,
            perUnit: !isNaN(nUnit) && nUnit > 0 && !isNaN(nPrice) ? nPrice / nUnit : 0,
        };
    }, [sellStockForm.partyId, sellStockForm.category, sellStockForm.stockId, sellStockForm.stockName, sellCategoryStocks]);

    useEffect(() => {
        // Compute expected sale amount as soon as sellUnits change
        let unitsNum = parseFloat(sellStockForm.sellUnits || '0');
        let {unit, amount} = sellStockAvailableDetails;
        let perUnit = unit > 0 ? amount / unit : 0;
        let calc = !isNaN(unitsNum) ? perUnit * unitsNum : 0;
        setSellStockFormAmount(calc);
    }, [sellStockForm, sellStockAvailableDetails]);

    const getNextCategoryId = () => {
        let allIds: number[] = [];
    
        stocksDataDb.forEach(row => {
            const dbItems = typeof row.categories === 'string'
                ? JSON.parse(row.categories)
                : row.categories;
    
            dbItems.forEach((item: any) => {
                if (item.id) {
                    allIds.push(Number(item.id));
                }
            });
        });
    
        const maxId = allIds.length > 0 ? Math.max(...allIds) : 0;
        return maxId + 1;
    };

    const handleSellStockFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        let patch: Partial<SellStockFormState> = { [name]: value };
        // Reset following fields on changes:
        if (name === "partyId") {
            patch.category = '';
            patch.stockId = '';
            patch.stockName = '';
            patch.sellUnits = '';
            patch.date = '';
        }
        if (name === "category") {
            patch.stockId = '';
            patch.stockName = '';
            patch.sellUnits = '';
        }
        if (name === "stockId") {
            // Find stock name for this stockId
            const found = sellCategoryStocks.find(s => String(s.id) === value);
            patch.stockName = found ? found.stock_name : '';
        }
        setSellStockForm(s => ({ ...s, ...patch }));
    };

    // Modal submit logic
    const handleSellStockSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!sellStockForm.partyId) {
            alert('Select party');
            return;
        }
        if (!sellStockForm.category) {
            alert('Select category');
            return;
        }
        if (!sellStockForm.stockId) {
            alert('Select stock');
            return;
        }
        let sellUnitsNumber = parseFloat(sellStockForm.sellUnits || '0');
        if (!sellStockForm.sellUnits || isNaN(sellUnitsNumber) || sellUnitsNumber <= 0) {
            alert('Please enter valid units to sell');
            return;
        }
        if (sellUnitsNumber > sellStockAvailableDetails.unit) {
            alert(
                `Not enough units available to sell! Available: ${sellStockAvailableDetails.unit}, Requested: ${sellUnitsNumber}`
            );
            return;
        }
        if (!sellStockForm.date) {
            alert("Select date for sell");
            return;
        }

        // Calculate amount
        const expectedAmount = sellStockFormAmount;

        // Compose API request (implementation specific!)
        setSellStockLoading(true);
        try {
            // This should be replaced with your real API endpoint.
            const payload = {
                party_id: sellStockForm.partyId ?? null,
                category: sellStockForm.category ?? null,
                stock_id: sellStockForm.stockId ?? null,
                stock_name: sellStockForm.stockName ?? null,
                sell_units: sellUnitsNumber ?? null,
                sell_amount: expectedAmount ?? null,
                indirect_charges: sellStockForm.indirectCharges ?? null,
                date: sellStockForm.date ?? null
            };
            const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/sellstocks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await res.json();
            if (!res.ok) throw new Error(result?.message || 'Failed to sell stock');
            alert('Stock sell recorded successfully!');
            setShowSellStockModal(false);
            setSellStockForm({
                partyId: '',
                category: '',
                stockId: '',
                stockName: '',
                sellUnits: '',
                sellAmount:"",
                indirectCharges:"",
                date: '',
            });
            // Refresh the page or fetch state as necessary
            window.location.reload();
        } catch (err: any) {
            alert('Error: ' + (err?.message ?? 'Something went wrong'));
        } finally {
            setSellStockLoading(false);
        }
    };

    // End --- MODAL for sell stock ----

    const fetchParties = useCallback(async () => {
        const res = await parties();
        const cleaned = Array.isArray(res)
            ? res.map((item: any) => ({
                ...item,
                categories:
                    typeof item.categories === 'string'
                        ? (() => {
                            try {
                                const parsed = JSON.parse(item.categories);
                                return Array.isArray(parsed) ? parsed : [item.categories];
                            } catch {
                                return [item.categories];
                            }
                        })()
                        : Array.isArray(item.categories)
                            ? item.categories
                            : [],
                total_amount: item.total_amount !== undefined ? Number(item.total_amount) : undefined,
            }))
            : [];
        setPartiesList(cleaned);
    }, []);
    const fetchStocksNames = async () => {
        
        try {
          const res = await fetchStocknames();
         
          const data = await res;
          setStocksnames(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setStocksnames([]);
        } finally {
          
        }
      };
    const fetchMutualNames = async () => {
        
        try {
          const res = await fetchMutualnames();
         
          const data = await res;
          setMutualsnames(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setMutualsnames([]);
        } finally {
          
        }
      };
    
    const fetchCategories = useCallback(async () => {
        const res = await categoriesApi();
        if (Array.isArray(res)) {
            setCategoriesFromApi(res);
            setPossibleCategories(res);
        }
    }, []);

    const fetchAllStocksForCategories = useCallback(async () => {
        const stocks: Stock[] = await fetchStocksAPI();
        setAllStocks(stocks);

        const byCategory: { [cat: string]: Stock[] } = {};
        stocks.forEach(stock => {
            if (!byCategory[stock.category]) byCategory[stock.category] = [];
            byCategory[stock.category].push(stock);
        });
        setPossibleStocksByCategory(byCategory);
    }, []);

    const fetchStocksDb = useCallback(async () => {
        try {
            const res = await stockes();
            setStocksDb(Array.isArray(res) ? res : []);
        } catch (e) {
            setStocksDb([]);
        }
    }, []);

    const fetchStocksDataDb = useCallback(async () => {
        try {
            const res = await stockdata();
            setStocksDataDb(Array.isArray(res) ? res : []);
        } catch (e) {
            setStocksDataDb([]);
        }
    }, []);

    useEffect(() => {
        fetchStocksNames();
        fetchMutualNames();
        fetchParties();
        fetchCategories();
        fetchAllStocksForCategories();
        fetchStocksDb();
        fetchStocksDataDb();
    }, [fetchParties, fetchCategories, fetchAllStocksForCategories, fetchStocksDb, fetchStocksDataDb]);

    useEffect(() => {
        if (data.partyId && partiesList.length > 0) {
            const party = partiesList.find(p => String(p.id) === String(data.partyId));
            if (party && party.total_amount !== undefined && party.total_amount !== null && !isNaN(Number(party.total_amount))) {
                setSelectedPartyTotalAmount(Number(party.total_amount));
            } else {
                setSelectedPartyTotalAmount(null);
            }
        } else {
            setSelectedPartyTotalAmount(null);
        }
    }, [data.partyId, partiesList]);

    // !! The available amount (after deduction) considers both direct payments and structure/indirect charges
    useEffect(() => {
        if (!selectedPartyTotalAmount) {
            setDeductedAvailable(null);
            return;
        }

        if (!data.partyId || !stocksDataDb.length) {
            setDeductedAvailable(selectedPartyTotalAmount);
            return;
        }

        // 1. Get total deducted amount up to now (from stockdata) including direct amounts
        const dbDeducted = calculateTotalDeductedIncludingDirect(stocksDataDb, data.partyId);

        let newDeducted = selectedPartyTotalAmount - dbDeducted;
        let overage = 0;

        if (investmentType === "direct") {
            if (directPaymentAmount && !isNaN(Number(directPaymentAmount))) {
                newDeducted -= Number(directPaymentAmount);
            }
            overage = -newDeducted;
            setDeductedAvailable(newDeducted);
        } else if (investmentType === "structure") {
            // sum of new entries (totalAmount + indirectCharges)
            let uiDeducted = 0;
            Object.entries(categoryEntries).forEach(([category, ents]) => {
                ents.forEach(ent => {
                    let amt = parseFloat(ent.totalAmount || "0");
                    if (isNaN(amt)) amt = 0;
                    let indirect = parseFloat(ent.indirectCharges || "0");
                    if (isNaN(indirect)) indirect = 0;
                    uiDeducted += amt + indirect;
                });
            });
            // support direct-to-structure transition: add pending direct (if any)
            let pendingDirect = directToStructureMeta ? Number(directToStructureMeta.directPaymentAmount) || 0 : 0;
            newDeducted = selectedPartyTotalAmount - dbDeducted - uiDeducted - pendingDirect;
            overage = -newDeducted;
            setDeductedAvailable(newDeducted);
        }

        // Only prompt user if overage (amount exceeds available), only once per session
        // If press Cancel, don't let them add: block by setting a marker and a disable flag
        if (
            newDeducted < 0 &&
            !exceedPromptedRef.current // Use ref to avoid repeated asks
        ) {
            setTimeout(() => {
                // eslint-disable-next-line no-alert
                const confirm = window.confirm("You exceed your amount. Do you want to add it from margin money?");
                if (confirm) {
                    exceedPromptedRef.current = true; // User allows, do not prompt anymore
                } else {
                    exceedPromptedRef.current = true; // Prevent further prompts
                    // Block adding: setDeductedAvailable to null to disable actions
                    setDeductedAvailable(null);
                }
            }, 100); // Slight timeout to ensure UI is updated
        }

    }, [
        selectedPartyTotalAmount,
        investmentType,
        directPaymentAmount,
        categoryEntries,
        data.partyId,
        stocksDataDb,
        directToStructureMeta
    ]);

    useEffect(() => {
        if (investmentType === "direct") setHasDirectPending(false);
        if (investmentType === "structure" && directToStructureMeta) setHasDirectPending(true);
    }, [investmentType, directToStructureMeta]);

    // --- REMOVE restriction: allow any allocation, only show "not enough" but don't block
    useEffect(() => {
        // No error/disable logic anymore
        setEntryErrors({});
    }, [categoryEntries, stocksDataDb, data.partyId, selectedPartyTotalAmount, investmentType, directToStructureMeta]);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setData(prev => ({
            ...prev,
            [name]: value,
        }));
        if (name === "partyId") {
            setInvestmentType('structure');
            setCategoryEntries({});
            setDirectPaymentAmount('');
            setDirectToStructureMeta(null);
            fetchUserRemainingBalance(value);
        }
        if (name === "directPaymentAmount") {
            setDirectPaymentAmount(value);
        }
    };

    const handleInvestmentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as 'direct' | 'structure';
        setInvestmentType(value);
        setDirectPaymentAmount('');
        setCategoryEntries({});
        setDirectToStructureMeta(null);
        setHasDirectPending(false);
    };

    const isChecked = (cat: CategoryApiType) => Object.prototype.hasOwnProperty.call(categoryEntries, cat.category_name);

    const handleCategory = (cat: CategoryApiType) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const copy = { ...prev };
            if (copy[category]) {
                delete copy[category];
            } else {
                copy[category] = [
                    emptyStockCategoryInfo(
                        category,
                        '',
                        ''
                    ),
                ];
            }
            return copy;
        });
    };

    const handleCategoryFieldChange = (
        cat: CategoryApiType,
        entryIdx: number,
        field: keyof StockCategoryInfo,
        value: string
    ) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            const entry = entries[entryIdx] ? { ...entries[entryIdx] } : emptyStockCategoryInfo(category);

            let autoCalculated = entry.autoCalculated;
            let newPerUnitCharge = entry.perUnitCharge;

            let total = field === "totalAmount" ? value : entry.totalAmount || "0";
            let indirect = field === "indirectCharges" ? value : entry.indirectCharges || "0";
            let unit = field === "unit" ? value : entry.unit || "0";

            let newEntry = { ...entry };

            if (field === 'stockName') {
                newEntry.stockName = value;

                if (value && value.trim()) {
                    const stock = allStocks.find(
                        s => s.category === category && (
                            String(s.name).toLowerCase() === String(value).toLowerCase() ||
                            String(s.id) === String(value)
                        )
                    );
                    if (stock) {
                        newEntry.stockId = stock.id;
                    } else {
                        newEntry.stockId = value;
                    }
                } else {
                    newEntry.stockId = '';
                }
            } else if (field === 'stockId') {
                newEntry.stockId = value;
                if (value && value.trim()) {
                    const stock = allStocks.find(
                        s => s.category === category && (
                            String(s.id) === String(value) ||
                            String(s.name).toLowerCase() === String(value).toLowerCase()
                        )
                    );
                    newEntry.stockName = stock ? stock.name : value;
                } else {
                    newEntry.stockName = '';
                }
            }

            // Fix: Allow perUnitCharge zero for ETF/ETFO category, otherwise as before.
            if (field === "unit" || field === "totalAmount" || field === "indirectCharges") {
                const unitN = parseFloat(unit);
                const totalAmountN = parseFloat(total || "0");
                const indirectChargesN = parseFloat(indirect || "0");
                if (unitN > 0) {
                    newPerUnitCharge = ((totalAmountN + indirectChargesN) / unitN).toFixed(2);
                    autoCalculated = true;
                } else {
                    newPerUnitCharge = '';
                    autoCalculated = true;
                }
                newEntry[field] = value;
                newEntry.perUnitCharge = newPerUnitCharge;
                newEntry.autoCalculated = autoCalculated;
            } else if (field === "perUnitCharge") {
                autoCalculated = false;
                newPerUnitCharge = value;
                newEntry.perUnitCharge = value;
                newEntry.autoCalculated = false;
            } else if (field !== "stockId" && field !== "stockName") {
                (newEntry as any)[field] = value;
            }

            entries[entryIdx] = newEntry;
            return { ...prev, [category]: entries };
        });
    };

    const handleAddEntry = (cat: CategoryApiType) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            return {
                ...prev,
                [category]: [
                    ...entries,
                    emptyStockCategoryInfo(
                        category,
                        '',
                        ''
                    ),
                ],
            };
        });
    };

    const handleRemoveEntry = (cat: CategoryApiType, entryIdx: number) => {
        const category = cat.category_name;
        setCategoryEntries(prev => {
            const entries = prev[category] ? [...prev[category]] : [];
            entries.splice(entryIdx, 1);
            const newEntries = entries.length === 0 ? undefined : entries;
            const copy: CategoryEntryMap = { ...prev };
            if (newEntries && newEntries.length > 0) {
                copy[category] = newEntries;
            } else {
                delete copy[category];
            }
            return copy;
        });
    };

    // ---- SUBMIT ---- (unchanged except removal of restriction)
    const handleSubmit2 = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!data.partyId) {
            alert('Please select a party');
            return;
        }
    
        let party: Party | undefined = partiesList.find(p => String(p.id) === String(data.partyId));
        let availableTotalAmount = (party && party.total_amount !== undefined) ? Number(party.total_amount) : null;
        if (availableTotalAmount == null) {
            alert("Could not determine total amount available.");
            return;
        }
    
        const isStructure = investmentType === 'structure';
        const selectedCategories = Object.keys(categoryEntries);
        if (selectedCategories.length === 0) {
            alert('Please select at least one category');
            return;
        }
    
        // Array to hold ONLY completely new stocks for the POST method
        let categoriesToSubmitForPost: any[] = [];
    
        for (const categoryName of selectedCategories) {
            const entries = categoryEntries[categoryName];
    
            for (const [idx, catInfo] of entries.entries()) {
                if (!catInfo.stockName || !catInfo.stockName.trim()) continue;
    
                // 1. Search for this stock in the current database state
                const existingRow = stocksDataDb.find(row => {
                    if (String(row.party_id) !== String(data.partyId)) return false;
                    let dbItems: any[] = typeof row.categories === 'string' ? JSON.parse(row.categories) : row.categories;
                    return dbItems.some(item => item.stock_name === catInfo.stockName && item.category === categoryName);
                });
    
                if (existingRow) {
                    // 2. STOCK EXISTS -> CALCULATE AND EXECUTE PUT IMMEDIATELY
                    let dbItems: any[] = typeof existingRow.categories === 'string' ? JSON.parse(existingRow.categories) : existingRow.categories;
                    let item = dbItems.find(i => i.stock_name === catInfo.stockName && i.category === categoryName);
    
                    const oldUnit = parseFloat(item.unit || "0");
                    const oldAmount = parseFloat(item.total_amount || "0");
                    const oldIndirect = parseFloat(item.indirect_charges || "0");
    
                    const newUnit = parseFloat(catInfo.unit || "0");
                    const newAmount = parseFloat(catInfo.totalAmount || "0");
                    const newIndirect = parseFloat(catInfo.indirectCharges || "0");
    
                    const totalUnit = oldUnit + newUnit;
                    const totalAmount = oldAmount + newAmount;
                    const totalIndirect = oldIndirect + newIndirect;
                    
                    // Recalculate average price per unit including indirect charges
                    const avgPricePerUnit = totalUnit > 0 ? ((totalAmount + totalIndirect) / totalUnit).toFixed(2) : "0.00";
    
                    const putBody = {
                        id: existingRow.id,                // The database row ID
                        cat_id: catInfo.id,               // The entry ID from the UI
                        name: catInfo.stockName,
                        party_id: data.partyId,
                        categories: JSON.stringify([{     // Updated categories array for the backend
                            ...item,
                            unit: totalUnit.toString(),
                            total_amount: totalAmount.toString(),
                            indirect_charges: totalIndirect.toString(),
                            perUnitCharge: avgPricePerUnit,
                            total_amount_category: totalAmount.toString()
                        }]),
                        category: categoryName,
                        unit: totalUnit.toString(),
                        price_per_unit: avgPricePerUnit,
                        total_amount: totalAmount.toString(),
                        indirect_charges: totalIndirect.toString(),
                        date: data.date || existingRow.date
                    };
    
                    try {
                        // Note: URL includes the ID as you requested earlier
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(putBody),
                        });
                        if (!res.ok) throw new Error(`Failed to update existing stock: ${catInfo.stockName}`);
                        // console.log(`Updated ${catInfo.stockName} via PUT`);
                    } catch (err) {
                        console.error("PUT Error:", err);
                    }
    
                } else {
                    // 3. STOCK DOES NOT EXIST -> COLLECT FOR POST
                    categoriesToSubmitForPost.push({
                        id: catInfo.id,
                        stock_name: catInfo.stockName,
                        unit: catInfo.unit,
                        total_amount: catInfo.totalAmount,
                        indirect_charges: catInfo.indirectCharges ?? "0",
                        perUnitCharge: catInfo.perUnitCharge,
                        category: categoryName,
                        investment_type: isStructure ? "structure" : "direct",
                        total_amount_category: catInfo.totalAmount
                    });
                }
            }
        }
    
        // 4. Final Step: Only fire POST if there are actual NEW stocks
        if (categoriesToSubmitForPost.length > 0) {
            const postPayload = {
                party_id: data.partyId,
                date: data.date,
                investment_type: isStructure ? 'structure' : 'direct',
                categories: categoriesToSubmitForPost
            };
    
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(postPayload),
                });
                if (!res.ok) throw new Error('Failed to create new stocks via POST');
                // console.log("Created new stocks via POST");
            } catch (err: any) {
                alert(err.message);
            }
        }
    
        alert('Submission process finished');
        window.location.reload();
    };
    const handleSubmitold = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        
        // 1. Initial Validation
        if (!data.partyId) {
            alert('Please select a party');
            return;
        }

        let party: Party | undefined = partiesList.find(p => String(p.id) === String(data.partyId));
        let availableTotalAmount = (party && party.total_amount !== undefined) ? Number(party.total_amount) : null;
        
        if (availableTotalAmount == null) {
            alert("Could not determine total amount available for the selected party.");
            return;
        }

        const selectedCategories = Object.keys(categoryEntries);
        if (selectedCategories.length === 0) {
            alert('Please select at least one category');
            return;
        }

        // ==========================================
        // CASE A: DIRECT PAYMENT LOGIC
        // ==========================================
        if (investmentType === 'direct' && !hasDirectPending) {
            if (!directPaymentAmount || isNaN(Number(directPaymentAmount)) || Number(directPaymentAmount) <= 0) {
                alert('Please enter a valid payment amount');
                return;
            }

            for (const categoryName of selectedCategories) {
                // Check if a Direct Payment record already exists for this party/category
                const existingDirectRow = stocksDataDb.find(row => {
                    if (String(row.party_id) !== String(data.partyId)) return false;
                    let dbItems = typeof row.categories === 'string' ? JSON.parse(row.categories) : row.categories;
                    return dbItems.some((item:any) => item.investment_type === "direct" && item.category === categoryName);
                });

                if (existingDirectRow) {
                    // UPDATE EXISTING DIRECT RECORD (PUT)
                    let dbItems = typeof existingDirectRow.categories === 'string' ? JSON.parse(existingDirectRow.categories) : existingDirectRow.categories;
                    let item = dbItems.find((i:any) => i.investment_type === "direct" && i.category === categoryName);

                    const oldAmount = parseFloat(item.total_amount || "0");
                    const newAmount = parseFloat(directPaymentAmount);
                    const totalAmt = (oldAmount + newAmount).toString();

                    const putBody = {
                        id: existingDirectRow.id,
                        cat_id: item.id || '',
                        name: "Direct Payment",
                        party_id: data.partyId,
                        categories: JSON.stringify([{
                            ...item,
                            total_amount: totalAmt,
                            total_amount_category: totalAmt,
                            investment_type: "direct"
                        }]),
                        category: categoryName,
                        unit: "0",
                        price_per_unit: "0",
                        total_amount: totalAmt,
                        indirect_charges: "0",
                        date: data.date || existingDirectRow.date
                    };

                    await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(putBody),
                    });
                } else {
                    // CREATE NEW DIRECT RECORD (POST)
                    const postPayload = {
                        party_id: data.partyId,
                        date: data.date ?? null,
                        investment_type: "direct",
                        direct_payment_amount: directPaymentAmount,
                        categories: [{
                            investment_type: "direct",
                            total_amount: Number(directPaymentAmount),
                            category: categoryName,
                            total_amount_category: directPaymentAmount
                        }]
                    };
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(postPayload),
                    });
                }
            }
            alert('Direct Payment processed');
            window.location.reload();
            return;
        }

        // ==========================================
        // CASE B: STRUCTURE PAYMENT LOGIC (STOCKS)
        // ==========================================
        let categoriesToSubmitForPost: any[] = [];
        const isStructure = investmentType === 'structure';

        for (const categoryName of selectedCategories) {
            const entries = categoryEntries[categoryName];

            for (const [idx, catInfo] of entries.entries()) {
                if (!catInfo.stockName || !catInfo.stockName.trim()) continue;

                // Find if stock already exists in DB
                const existingStockRow = stocksDataDb.find(row => {
                    if (String(row.party_id) !== String(data.partyId)) return false;
                    let dbItems = typeof row.categories === 'string' ? JSON.parse(row.categories) : row.categories;
                    return dbItems.some((item:any) => item.stock_name === catInfo.stockName && item.category === categoryName);
                });

                if (existingStockRow) {
                    // UPDATE EXISTING STOCK (PUT)
                    let dbItems = typeof existingStockRow.categories === 'string' ? JSON.parse(existingStockRow.categories) : existingStockRow.categories;
                    let item = dbItems.find((i:any) => i.stock_name === catInfo.stockName && i.category === categoryName);

                    const oldUnit = parseFloat(item.unit || "0");
                    const oldAmt = parseFloat(item.total_amount || "0");
                    const oldInd = parseFloat(item.indirect_charges || "0");

                    const newUnit = parseFloat(catInfo.unit || "0");
                    const newAmt = parseFloat(catInfo.totalAmount || "0");
                    const newInd = parseFloat(catInfo.indirectCharges || "0");

                    const totalU = oldUnit + newUnit;
                    const totalA = oldAmt + newAmt;
                    const totalI = oldInd + newInd;
                    const avgPrice = totalU > 0 ? ((totalA + totalI) / totalU).toFixed(2) : "0.00";

                    const putBody = {
                        id: existingStockRow.id,
                        cat_id: catInfo.id,
                        name: catInfo.stockName,
                        party_id: data.partyId,
                        categories: JSON.stringify([{
                            ...item,
                            unit: totalU.toString(),
                            total_amount: totalA.toString(),
                            indirect_charges: totalI.toString(),
                            perUnitCharge: avgPrice,
                            total_amount_category: totalA.toString()
                        }]),
                        category: categoryName,
                        unit: totalU.toString(),
                        price_per_unit: avgPrice,
                        total_amount: totalA.toString(),
                        indirect_charges: totalI.toString(),
                        date: data.date || existingStockRow.date
                    };

                    try {
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(putBody),
                        });
                        if (!res.ok) throw new Error(`Update failed for ${catInfo.stockName}`);
                    } catch (err) { console.error(err); }

                } else {
                    // NEW STOCK (COLLECT FOR POST)
                    categoriesToSubmitForPost.push({
                        id: catInfo.id,
                        stock_name: catInfo.stockName,
                        unit: catInfo.unit,
                        total_amount: catInfo.totalAmount,
                        indirect_charges: catInfo.indirectCharges ?? "0",
                        perUnitCharge: catInfo.perUnitCharge,
                        category: categoryName,
                        investment_type: isStructure ? "structure" : "direct",
                        total_amount_category: catInfo.totalAmount
                    });
                }
            }
        }

        // Final POST call for all completely new items
        if (categoriesToSubmitForPost.length > 0) {
            const finalPayload = {
                party_id: data.partyId,
                date: (hasDirectPending && directToStructureMeta) ? directToStructureMeta.metaDate : data.date,
                investment_type: isStructure ? 'structure' : 'direct',
                categories: categoriesToSubmitForPost
            };
            // if (hasDirectPending && directToStructureMeta) finalPayload.direct_payment_amount = directToStructureMeta.directPaymentAmount;

            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(finalPayload),
                });
                if (!res.ok) throw new Error('Post failed');
            } catch (err: any) { alert(err.message); }
        }

        alert('Process completed successfully');
        window.location.reload();
    };
    const handleSubmitworkingwell = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!data.partyId) {
            alert('Please select a party');
            return;
        }

        let party: Party | undefined = partiesList.find(p => String(p.id) === String(data.partyId));
        let availableTotalAmount = (party && party.total_amount !== undefined) ? Number(party.total_amount) : null;
        if (availableTotalAmount == null) {
            alert("Could not determine total amount available.");
            return;
        }

        const isStructure = investmentType === 'structure';
        const selectedCategories = Object.keys(categoryEntries);
        if (selectedCategories.length === 0) {
            alert('Please select at least one category');
            return;
        }

        // Map to group updates by Row ID to prevent overwriting when updating multiple stocks in one row
        const rowUpdates = new Map();
        let categoriesToSubmitForPost: any[] = [];

        // Track the sum of all total_amounts being added/updated
        let grandTotalAmount = 0;

        for (const categoryName of selectedCategories) {
            const entries = categoryEntries[categoryName];

            for (const catInfo of entries) {
                if (!catInfo.stockName || !catInfo.stockName.trim()) continue;

                // Grab this stock's total_amount for summing, if defined
                const currentTotalAmount = parseFloat(catInfo.totalAmount || "0");
                if (!isNaN(currentTotalAmount)) {
                    grandTotalAmount += currentTotalAmount;
                }
                const currentIndirectCharges = parseFloat(catInfo.indirectCharges || "0");
                if (!isNaN(currentIndirectCharges)) {
                    grandTotalAmount += currentIndirectCharges;
                }

                // 1. Find the existing row in the DB
                const existingRow = stocksDataDb.find(row => {
                    if (String(row.party_id) !== String(data.partyId)) return false;
                    let dbItems: any[] = typeof row.categories === 'string' ? JSON.parse(row.categories) : row.categories;
                    return dbItems.some(item => item.stock_name === catInfo.stockName && item.category === categoryName);
                });

                if (existingRow) {
                    // 2. Get the current state of this row's array (either from our local map or the DB)
                    let currentArray = rowUpdates.has(existingRow.id)
                        ? rowUpdates.get(existingRow.id).updatedCategories
                        : (typeof existingRow.categories === 'string' ? JSON.parse(existingRow.categories) : [...existingRow.categories]);

                    let targetStock = currentArray.find((i: any) => i.stock_name === catInfo.stockName && i.category === categoryName);

                    if (targetStock) {
                        // --- CALCULATE UPDATED VALUES ---
                        const oldUnit = parseFloat(targetStock.unit || "0");
                        const oldAmount = parseFloat(targetStock.total_amount || "0");
                        const oldIndirect = parseFloat(targetStock.indirect_charges || "0");

                        const newUnit = parseFloat(catInfo.unit || "0");
                        const newAmount = parseFloat(catInfo.totalAmount || "0");
                        const newIndirect = parseFloat(catInfo.indirectCharges || "0");

                        const totalUnit = oldUnit + newUnit;
                        const totalAmount = oldAmount + newAmount;
                        const totalIndirect = oldIndirect + newIndirect;

                        // MAINTAIN PRICE PER UNIT (Weighted Average)
                        // (Total Investment cost / Total Units)
                        const maintainedPricePerUnit = totalUnit > 0
                            ? ((totalAmount + totalIndirect) / totalUnit).toFixed(2)
                            : "0.00";

                        // Update the stock item inside the array
                        targetStock.unit = totalUnit.toString();
                        targetStock.total_amount = totalAmount.toString();
                        targetStock.indirect_charges = totalIndirect.toString();
                        targetStock.perUnitCharge = maintainedPricePerUnit;
                        targetStock.total_amount_category = totalAmount.toString();

                        // Update the Map with grouped data for this Row ID
                        rowUpdates.set(existingRow.id, {
                            updatedCategories: currentArray,
                            lastCatId: catInfo.id,
                            lastName: catInfo.stockName,
                            lastCategory: categoryName,
                            lastUnit: totalUnit.toString(),
                            lastPrice: maintainedPricePerUnit, // This is the updated price per unit
                            lastTotal: totalAmount.toString(),
                            lastIndirect: totalIndirect.toString()
                        });
                    }
                } else {
                    // 3. NEW STOCK -> COLLECT FOR POST
                    categoriesToSubmitForPost.push({
                        id: catInfo.id,
                        stock_name: catInfo.stockName,
                        unit: catInfo.unit,
                        total_amount: catInfo.totalAmount,
                        indirect_charges: catInfo.indirectCharges ?? "0",
                        perUnitCharge: catInfo.perUnitCharge, // UI calculated value for new stocks
                        category: categoryName,
                        investment_type: isStructure ? "structure" : "direct",
                        total_amount_category: catInfo.totalAmount
                    });
                }
            }
        }

        // Store the final grand total in setTotalAmmountSpend for global reporting
        setTotalAmmountSpend(grandTotalAmount);

        // ====== USERMETA update logic =========

        const handleUserMetaUpdate = async () => {
            // 1. Check if there's an existing meta record for this party_id and meta_key "remaining"
            try {
                const usermetaResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta?party_id=${data.partyId}`);
                const usermetaList = await usermetaResponse.json();
                // Find meta with meta_key "remaining"
                const foundMeta = Array.isArray(usermetaList)
                    ? usermetaList.find((meta: any) => meta.meta_key === "remaining" && String(meta.party_id) === String(data.partyId))
                    : undefined;

                if (foundMeta) {
                    // Update: remaining = oldvalue - grandTotalAmount
                    const oldVal = parseFloat(foundMeta.meta_value || "0");
                    const meta_value = (oldVal - grandTotalAmount).toString();
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta/${foundMeta.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            meta_key: "remaining",
                            meta_value,
                            party_id: data.partyId,
                        })
                    });
                } else {
                    // Create a new usermeta record with key "remaining"
                    const remaining = ((Number(totalamountspend) || 0) - grandTotalAmount).toString();
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            meta_key: "remaining",
                            meta_value: remaining,
                            party_id: data.partyId,
                        })
                    });
                }
            } catch (err) {
                console.error("Usermeta API failed", err);
            }
        };

        // --- API EXECUTION ---

        // A. PUT UPDATES (For grouped existing stocks)
        for (const [rowId, dataObj] of rowUpdates.entries()) {
            const putBody = {
                id: rowId,
                cat_id: dataObj.lastCatId,
                name: dataObj.lastName,
                party_id: data.partyId || data.partyId,
                categories: JSON.stringify(dataObj.updatedCategories),
                category: dataObj.lastCategory,
                unit: dataObj.lastUnit,
                price_per_unit: dataObj.lastPrice, // Correctly maintained weighted average
                total_amount: dataObj.lastTotal,
                indirect_charges: dataObj.lastIndirect,
                date: data.date || ""
            };

            try {
                await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(putBody),
                });
            } catch (err) {
                console.error("Update failed", err);
            }
        }

        // B. POST NEW RECORDS
        if (categoriesToSubmitForPost.length > 0) {
            const postPayload = {
                party_id: data.partyId,
                date: data.date,
                investment_type: isStructure ? 'structure' : 'direct',
                categories: categoriesToSubmitForPost
            };

            try {
                await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(postPayload),
                });
            } catch (err) {
                console.error("Post failed", err);
            }
        }

        // C. USERMETA call for "remaining" value
        await handleUserMetaUpdate();

        alert('Submission finished successfully');
        window.location.reload();
    };
    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!data.partyId) {
            alert('Please select a party');
            return;
        }

        let party: Party | undefined = partiesList.find(p => String(p.id) === String(data.partyId));
        let availableTotalAmount = (party && party.total_amount !== undefined) ? Number(party.total_amount) : null;

        if (availableTotalAmount == null) {
            alert("Could not determine total amount available.");
            return;
        }

        // Determine current investment type from state
        const currentInvestmentType = investmentType === 'structure' ? 'structure' : 'direct';
        const selectedCategories = Object.keys(categoryEntries);

        if (selectedCategories.length === 0) {
            alert('Please select at least one category');
            return;
        }

        const rowUpdates = new Map();
        let categoriesToSubmitForPost: any[] = [];
        let grandTotalAmount = 0;

        // Gather all category data for invoice: we want the same structure as `categoriesToSubmitForPost` usually
        let datainvoiceAll: any[] = [];

        for (const categoryName of selectedCategories) {
            const entries = categoryEntries[categoryName];

            for (const catInfo of entries) {
                if (!catInfo.stockName || !catInfo.stockName.trim()) continue;

                // Track Total Spend (Amount + Indirect Charges)
                const currentTotalAmount = parseFloat(catInfo.totalAmount || "0");
                const currentIndirectCharges = parseFloat(catInfo.indirectCharges || "0");
                grandTotalAmount += (currentTotalAmount + currentIndirectCharges);

                // Invoice data: push all for datainvoice
                datainvoiceAll.push({
                    id: catInfo.id,
                    stock_name: catInfo.stockName,
                    unit: catInfo.unit,
                    total_amount: catInfo.totalAmount,
                    indirect_charges: catInfo.indirectCharges ?? "0",
                    perUnitCharge: catInfo.perUnitCharge,
                    category: categoryName,
                    investment_type: currentInvestmentType,
                    total_amount_category: catInfo.totalAmount
                });

                // 1. Find the existing row in the DB to decide between PUT or POST
                const existingRow = stocksDataDb.find(row => {
                    if (String(row.party_id) !== String(data.partyId)) return false;
                    let dbItems: any[] = typeof row.categories === 'string' ? JSON.parse(row.categories) : row.categories;
                    // Check if stock name AND category match in the same row
                    return dbItems.some(item => item.stock_name === catInfo.stockName && item.category === categoryName);
                });

                if (existingRow) {
                    // 2. PREPARE PUT DATA (Add to existing stock)
                    let currentArray = rowUpdates.has(existingRow.id)
                        ? rowUpdates.get(existingRow.id).updatedCategories
                        : (typeof existingRow.categories === 'string' ? JSON.parse(existingRow.categories) : [...existingRow.categories]);

                    let targetStock = currentArray.find((i: any) => i.stock_name === catInfo.stockName && i.category === categoryName);

                    if (targetStock) {
                        const oldUnit = parseFloat(targetStock.unit || "0");
                        const oldAmount = parseFloat(targetStock.total_amount || "0");
                        const oldIndirect = parseFloat(targetStock.indirect_charges || "0");

                        const newUnit = parseFloat(catInfo.unit || "0");
                        const newAmount = parseFloat(catInfo.totalAmount || "0");
                        const newIndirect = parseFloat(catInfo.indirectCharges || "0");

                        const totalUnit = oldUnit + newUnit;
                        const totalAmount = oldAmount + newAmount;
                        const totalIndirect = oldIndirect + newIndirect;

                        // Weighted Average Calculation
                        const maintainedPricePerUnit = totalUnit > 0
                            ? ((totalAmount + totalIndirect) / totalUnit).toFixed(2)
                            : "0.00";

                        targetStock.unit = totalUnit.toString();
                        targetStock.total_amount = totalAmount.toString();
                        targetStock.indirect_charges = totalIndirect.toString();
                        targetStock.perUnitCharge = maintainedPricePerUnit;
                        targetStock.total_amount_category = totalAmount.toString();
                        // Ensure the investment_type is maintained/updated correctly
                        targetStock.investment_type = currentInvestmentType;

                        rowUpdates.set(existingRow.id, {
                            updatedCategories: currentArray,
                            lastCatId: catInfo.id,
                            lastName: catInfo.stockName,
                            lastCategory: categoryName,
                            lastUnit: totalUnit.toString(),
                            lastPrice: maintainedPricePerUnit,
                            lastTotal: totalAmount.toString(),
                            lastIndirect: totalIndirect.toString()
                        });
                    }
                } else {
                    // 3. PREPARE POST DATA (Completely new stock)
                    categoriesToSubmitForPost.push({
                        id: catInfo.id,
                        stock_name: catInfo.stockName,
                        unit: catInfo.unit,
                        total_amount: catInfo.totalAmount,
                        indirect_charges: catInfo.indirectCharges ?? "0",
                        perUnitCharge: catInfo.perUnitCharge,
                        category: categoryName,
                        investment_type: currentInvestmentType, // Set dynamically
                        total_amount_category: catInfo.totalAmount
                    });
                }
            }
        }

        // Optional: Global state update if you use this elsewhere
        if (typeof setTotalAmmountSpend === 'function') setTotalAmmountSpend(grandTotalAmount);

        // Send grandTotalAmount to buysellrecord API as totalpurchase, with totalsell and closingBLC as 0
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/auth/buysellrecord`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    date: data.date,
                    totalpurchase: grandTotalAmount,
                    totalsell: 0,
                    closingBLC: 0
                })
            });
        } catch (err) {
            // Log, but don't block user
            console.error("Failed to send buysellrecord (addstock)", err);
        }

        // Prepare invoice data and send invoice API call here (for every submission except direct payment which is handled separately)
        const invoicePayload = {
            farm: farmName,
            party: data.partyId,
            invoice_type: "purchase",
            date: data.date,
            datainvoice: datainvoiceAll
        };
        try {
            await fetch(`${process.env.REACT_APP_API_URL}/auth/invoice`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(invoicePayload)
            });
        } catch (err) {
            console.error('Error adding invoice:', err);
            // Optionally alert, but do not block main flow on error
        }

        // Balance update logic
        const handleUserMetaUpdate = async (amountToDeduct: number) => {
            try {
                const usermetaResponse = await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta?party_id=${data.partyId}`);
                const usermetaList = await usermetaResponse.json();
                const foundMeta = Array.isArray(usermetaList)
                    ? usermetaList.find((meta: any) => meta.meta_key === "remaining" && String(meta.party_id) === String(data.partyId))
                    : undefined;

                if (foundMeta) {
                    const oldVal = parseFloat(foundMeta.meta_value || "0");
                    const meta_value = (oldVal - amountToDeduct).toString();
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta/${foundMeta.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ meta_key: "remaining", meta_value, party_id: data.partyId })
                    });
                } else {
                    // remaining = Initial Party Amount - Spend
                    const remaining = ((availableTotalAmount || 0) - amountToDeduct).toString();
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/updateusermeta`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ meta_key: "remaining", meta_value: remaining, party_id: data.partyId })
                    });
                }
            } catch (err) {
                console.error("Usermeta update failed", err);
            }
        };

        if (investmentType === 'direct') {
            const payment = safeNumber(directPaymentAmount);

            if (payment <= 0) {
                alert("Please enter valid payment amount");
                return;
            }

            const nextId = getNextCategoryId();

            // 👉 Create virtual category entry
            const directCategory = [{
                id: nextId,
                stock_name: "DirectPayment", // or dynamic if needed
                unit: "0",
                total_amount: payment.toString(),
                category: "Equity",
                indirect_charges: "0",
                perUnitCharge: "0",
                investment_type: "direct",
                total_amount_category: payment.toString()
            }];

            try {
                await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        party_id: data.partyId,
                        date: data.date,
                        investment_type: "direct",
                        categories: directCategory
                    }),
                });

                // Also send invoice for direct payment case:
                const invoiceDirectPayload = {
                    farm: "Rajnish",
                    party: data.partyId,
                    invoice_type: "purchase",
                    date: data.date,
                    datainvoice: JSON.stringify(directCategory)
                };
                try {
                    await fetch(`${process.env.REACT_APP_API_URL}/auth/invoice`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(invoiceDirectPayload)
                    });
                } catch (err) {
                    // Only log, don't interrupt flow
                    console.error('Error adding direct payment invoice:', err);
                }

                await handleUserMetaUpdate(payment);

                alert("Direct payment submitted");
                window.location.reload();
                return;
            } catch (err) {
                console.error(err);
                alert("Error in direct payment");
                return;
            }
        }
        // EXECUTION
        try {
            // A. PUT UPDATES
            for (const [rowId, dataObj] of rowUpdates.entries()) {
                const putBody = {
                    id: rowId,
                    party_id: data.partyId,
                    categories: JSON.stringify(dataObj.updatedCategories),
                    // Fields usually required by the single-row schema fallback
                    name: dataObj.lastName,
                    category: dataObj.lastCategory,
                    unit: dataObj.lastUnit,
                    price_per_unit: dataObj.lastPrice,
                    total_amount: dataObj.lastTotal,
                    indirect_charges: dataObj.lastIndirect,
                    investment_type: currentInvestmentType,
                    date: data.date || new Date().toISOString().split('T')[0]
                };

                await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(putBody),
                });
            }

            // B. POST NEW RECORDS
            if (categoriesToSubmitForPost.length > 0) {
                const postPayload = {
                    party_id: data.partyId,
                    date: data.date,
                    investment_type: currentInvestmentType,
                    categories: categoriesToSubmitForPost
                };

                await fetch(`${process.env.REACT_APP_API_URL}/auth/addstocks`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(postPayload),
                });
            }

            await handleUserMetaUpdate(grandTotalAmount);
            alert('Submission successful');
            window.location.reload();
        } catch (err) {
            console.error("Submission failed", err);
            alert("An error occurred during submission.");
        }
    };
    // Withdrawal logic (unchanged) --
    useEffect(() => {
        debugger;
        if (!withdrawal.partyId) {
            setWithdrawCategoriesDb([]);
            setWithdrawStocksDb([]);
            setWithdrawalInvestmentType('structure');
            setWithdrawalDirectPayment('');
            return;
        }
        const relevantDbRows: PartyStockDBType[] = (stocksDataDb || []).filter(row =>
            String(row.party_id) === String(withdrawal.partyId)
        );
        let newCats: string[] = [];
        let newStocksDb: StockDBCategoryItem[] = [];
        for (const row of relevantDbRows) {
            let catsArr: StockDBCategoryItem[] = [];
            if (typeof row.categories === 'string') {
                try {
                    const parsed = JSON.parse(row.categories);
                    // Only include items that look like StockDBCategoryItem (i.e., have required keys)
                    catsArr = Array.isArray(parsed) ? parsed.filter(item =>
                        typeof item === "object" &&
                        item !== null &&
                        "category" in item &&
                        "stock_name" in item &&
                        "unit" in item &&
                        "total_amount" in item &&
                        "indirect_charges" in item &&
                        "perUnitCharge" in item
                    ) : [];
                } catch (e) {
                    catsArr = [];
                }
            } else if (Array.isArray(row.categories)) {
                // Only include objects that match shape of StockDBCategoryItem
                catsArr = (row.categories as any[]).filter(item =>
                    typeof item === "object" &&
                    item !== null &&
                    "category" in item &&
                    "stock_name" in item &&
                    "unit" in item &&
                    "total_amount" in item &&
                    "indirect_charges" in item &&
                    "perUnitCharge" in item
                ) as StockDBCategoryItem[];
            }
            debugger;
            catsArr.forEach(item => {
                if (item.category && !newCats.includes(item.category)) {
                    newCats.push(item.category);
                }
                newStocksDb.push(item);
            });
        }
        setWithdrawCategoriesDb(newCats);
        setWithdrawStocksDb(newStocksDb);
        setWithdrawalInvestmentType('structure');
        setWithdrawalDirectPayment('');
    }, [withdrawal.partyId, stocksDb]);

    const withdrawalStockOptions = useMemo<StockDBCategoryItem[]>(() => {
        if (!withdrawal.partyId || !withdrawal.category) return [];
        return withdrawStocksDb.filter(item => item.category === withdrawal.category);
    }, [withdrawal.partyId, withdrawal.category, withdrawStocksDb]);

    const withdrawalUnitLimit: number = useMemo(() => {
        if (!withdrawal.partyId || !withdrawal.category || !withdrawal.stockName) return 0;
        const item = withdrawalStockOptions.find(
            s => s.stock_name === withdrawal.stockName
        );
        let unit = 0;
        if (item && item.unit) {
            const n = parseFloat(item.unit);
            if (!isNaN(n)) unit = n;
        }
        return unit;
    }, [withdrawal.partyId, withdrawal.category, withdrawal.stockName, withdrawalStockOptions]);

    const handleWithdrawalInput = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        if (name === 'withdrawalInvestmentType') {
            setWithdrawalInvestmentType(value as 'direct' | 'structure');
            setWithdrawalDirectPayment('');
            setWithdrawal((prev) => ({
                ...prev,
                category: '',
                stockId: '',
                stockName: '',
                sale: '',
                date: '',
            }));
            return;
        }

        setWithdrawal(prev => {
            let changes: any = { [name]: value };
            if (name === "partyId") {
                changes = { ...changes, category: '', stockId: '', stockName: '', sale: '', date: '' };
            }
            if (name === "category") {
                changes = { ...changes, stockId: '', stockName: '', sale: '' };
            }
            if (name === "stockName") {
                let foundStock = withdrawalStockOptions.find(item => item.stock_name === value);
                changes.stockId = foundStock ? foundStock.id : '';
            }
            return { ...prev, ...changes };
        });
    };

    const resetWithdrawalForm = () => {
        setWithdrawal({
            partyId: '',
            category: '',
            stockId: '',
            stockName: '',
            sale: '',
            date: '',
        });
        setWithdrawalInvestmentType('structure');
        setWithdrawalDirectPayment('');
    };

    const handleWithdrawalSubmit = async (e?: React.FormEvent) => {
        debugger;
        if (e) e.preventDefault();
       

        if (!withdrawal.partyId) {
            alert("Please select a party for withdrawal.");
            return;
        }

        if (withdrawalInvestmentType === 'direct') {
            if (!withdrawalDirectPayment || isNaN(Number(withdrawalDirectPayment)) || Number(withdrawalDirectPayment) <= 0) {
                alert('Please enter a valid payment amount');
                return;
            }
            const payload = {
                party_id: withdrawal.partyId,
                date: withdrawal.date || null,
                amount: withdrawalDirectPayment,
                categories: JSON.stringify([{ investment_type: 'direct' }])
            };
            setWithdrawalLoading(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/auth/addwithdrawal`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    }
                );
                const result = await res.json();
                if (!res.ok) {
                    throw new Error(result?.message || "Failed to add withdrawal");
                }
                alert("Withdrawal added successfully!");
                resetWithdrawalForm();
                setShowWithdrawalModal(false);
                // RELOAD PAGE after withdrawal submit
                window.location.reload();
            } catch (err: any) {
                alert("Error: " + (err?.message ?? "Something went wrong"));
            } finally {
                setWithdrawalLoading(false);
            }
            return;
        }

        if (!withdrawal.category) {
            alert("Please select a category.");
            return;
        }
        if (!withdrawal.stockName) {
            alert("Please select a stock.");
            return;
        }
        if (!withdrawal.stockId) {
            alert("Invalid or missing stock id.");
            return;
        }
       
        if (!withdrawal.date) {
            alert("Please select a date.");
            return;
        }
        const saleValue = parseFloat(withdrawal.sale);
        if (withdrawalUnitLimit > 0 && saleValue > withdrawalUnitLimit) {
            alert(
                `You don't have enough unit for this withdrawal. Available unit: ${withdrawalUnitLimit}, you entered: ${withdrawal.sale}`
            );
            return;
        }
        if (withdrawalUnitLimit === 0) {
            alert(
                `No units found for this stock under the selected party and category.`
            );
            return;
        }
        if (!withdrawal.stockId || !String(withdrawal.stockId).trim()) {
            alert("Stock ID cannot be blank.");
            return;
        }

        // Build categories as an array with all required fields and stringify
        const categoriesArr = [
            {
                category: withdrawal.category,
                stockName: withdrawal.stockName,
                investment_type: 'indirect'
            }
        ];

        const payload = {
            party_id: withdrawal.partyId,
            categories: JSON.stringify(categoriesArr),
            stock_id: withdrawal.stockId,
            amount: withdrawalDirectPayment,
            date: withdrawal.date,
            investment_type: 'structure',
        };

        setWithdrawalLoading(true);

        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/auth/addwithdrawal`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const result = await res.json();
            if (!res.ok) {
                throw new Error(result?.message || "Failed to add withdrawal");
            }
            alert("Withdrawal added successfully!");
            resetWithdrawalForm();
            setShowWithdrawalModal(false);
            // RELOAD PAGE after withdrawal submit
            window.location.reload();
        } catch (err: any) {
            alert("Error: " + (err?.message ?? "Something went wrong"));
        } finally {
            setWithdrawalLoading(false);
        }
    };

    return (
        <>
            <div className='p-6'>
                <div className='w-full flex justify-between'>
                    <h1 className='text-[28px] text-[#101828] font-[500]'>Add new stock</h1>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                            onClick={() => window.location.href = '/withdraw'}
                        >
                            Add Withdrawal
                        </button>
                        <button
                            type="button"
                            className="flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#FF9500] to-[#1CAC70] py-2 rounded-[8px] px-8"
                            onClick={() => window.location.href = '/sell-stock'}
                        >
                            Sell Stock
                        </button>
                    </div>
                </div>
                <form
                    className='bg-[#ffff] p-5 w-full rounded-[10px] mt-4'
                    onSubmit={handleSubmit}
                    autoComplete="off"
                >
                    <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                        {/* Party selection */}
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="partyId">Party Name</label>
                            <br />
                            <select
                                name="partyId"
                                id="partyId"
                                value={data.partyId || urlParty}
                                onChange={handleInput}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                required
                            >
                                <option value="">Choose Party</option>
                                {partiesList.map((p) => (
                                    <option key={p.id} value={String(p.id)}>
                                        {p.name}
                                    </option>
                                ))}
                            </select>
                            {selectedPartyTotalAmount !== null && (
                                <div className="text-xs text-blue-600 mt-1">
                                    Total Available Amount: {selectedPartyTotalAmount}
                                </div>
                            )}
                            {deductedAvailable !== null && (
                                <div className="text-xs mt-1" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                    Available After Deduction: {deductedAvailable}
                                </div>
                            )}
                        </div>
                        {/* Date of Purchase */}
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="date">Date Of Purchase</label>
                            <br />
                            <input
                                type="date"
                                name="date"
                                id="date"
                                placeholder='Purchase date'
                                value={data.date}
                                onChange={handleInput}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                autoComplete="off"
                            />
                        </div>
                        {/* Investment Type */}
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                        <div className='w-[100%] md:w-[48%]'>
                            <label htmlFor="investmentType">Investment</label>
                            <br />
                            <select
                                name="investmentType"
                                id="investmentType"
                                value={investmentType}
                                onChange={handleInvestmentTypeChange}
                                className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                required
                            >
                                <option value="direct">Direct Payment</option>
                                <option value="structure">Structure Payment</option>
                            </select>
                        </div>
                    </div>
                    {investmentType === 'direct' && (
                        <div className='flex flex-wrap w-full justify-between mt-4 gap-4'>
                            <div className='w-[100%] md:w-[48%]'>
                                <label htmlFor="directPaymentAmount">Payment Amount</label>
                                <br />
                                <input
                                    type="number"
                                    name="directPaymentAmount"
                                    id="directPaymentAmount"
                                    placeholder="Enter payment amount"
                                    value={directPaymentAmount}
                                    min={0}
                                    step="any"
                                    onChange={e => setDirectPaymentAmount(e.target.value)}
                                    className='w-[100%] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    required
                                />
                                {selectedPartyTotalAmount !== null && !!directPaymentAmount && (
                                    <div className="text-xs mt-1"
                                        style={{
                                            color: Number(directPaymentAmount) > (selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId)) ? "#D8000C" : "#007200"
                                        }}>
                                        {Number(directPaymentAmount) > (selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId))
                                            ? `Not enough available. You are over by ${Number(directPaymentAmount) - (selectedPartyTotalAmount - calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId))}`
                                            : `You have enough amount.`}
                                    </div>
                                )}
                                <p className='mt-5 w-full'>Categories</p>
                                <div className="flex flex-wrap gap-4 p-3 bg-gradient-to-r from-[#F9FAFB] to-[#F4F2FF] rounded-xl shadow-sm border border-[#ececec] w-full">
                                    {possibleCategories.map((cat) => (
                                        <div
                                            key={cat.id}
                                            className={`flex items-center gap-2 py-2 px-4 rounded-lg transition-all duration-200 cursor-pointer border-2 ${
                                                isChecked(cat)
                                                    ? "bg-gradient-to-r from-[#1CAC70]/10 to-[#FF9500]/10 border-[#FF9500] shadow-md text-[#FF9500] font-semibold"
                                                    : "bg-white border-[#ececec] hover:shadow-lg text-[#344054]"
                                            }`}
                                            style={{ minWidth: 180 }}
                                            onClick={() => handleCategory(cat)}
                                            tabIndex={0}
                                            role="button"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked(cat)}
                                                onChange={() => handleCategory(cat)}
                                                className="form-checkbox h-5 w-5 accent-[#FF9500] rounded focus:ring-2 focus:ring-[#FF9500] transition"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => e.stopPropagation()}
                                            />
                                            <span className="text-[16px]">{cat.category_name}</span>
                                        </div>
                                    ))}
                                </div>
                                {deductedAvailable !== null && (
                                    <div className="text-xs mt-1" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                        Available After Deduction: {deductedAvailable}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {investmentType === 'structure' && (
                        <>
                            <p className='mt-5 w-full'>Categories</p>
                            <div className="flex flex-col gap-2 p-2">
                                {possibleCategories.map((cat) => {
                                    const entries = categoryEntries[cat.category_name] || [];
                                    return (
                                        <div key={cat.id} className="flex flex-col gap-1">
                                            <label className="flex items-center gap-2 cursor-pointer text-[#344054] text-[16px]">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked(cat)}
                                                    onChange={() => handleCategory(cat)}
                                                />
                                                {cat.category_name}
                                            </label>
                                            {isChecked(cat) && (
                                                <div className="flex flex-col gap-3 ml-5 w-full border-l-[2px] border-[#ececec] pl-4 mt-2 mb-2">
                                                    {(entries || []).map((entry, entryIdx) => {
                                                        // Restriction and disabling logic removed
                                                        return (
                                                            <div key={`${cat.category_name}-entry-${entry.id}`} className="flex flex-wrap gap-2 items-end w-full relative">
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`stockid-${cat.category_name}-${entryIdx}`} className="text-[13px]">ID</label>
                                                                    <input
                                                                        id={`stockid-${cat.category_name}-${entryIdx}`}
                                                                        type="text"
                                                                        value={entry.id}
                                                                        readOnly
                                                                        className="w-[60px] bg-gray-50 border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#5E5E5E] text-[13px] outline-none"
                                                                    />
                                                                </div>
                                                                <div className="flex flex-col">
                                                                  <label
                                                                    htmlFor={`stockname-${cat.category_name}-${entryIdx}`}
                                                                    className="text-[13px]"
                                                                  >
                                                                    Stock Name
                                                                  </label>
                                                                  <Select
  options={
    cat.category_name === "Mutul Fund"
      ? mutualsnames.map(stock => ({ value: stock.name, label: stock.name }))
      : stocksnames.map(stock => ({ value: stock.name, label: stock.name }))
  }
  value={
    entry.stockName
      ? { value: entry.stockName, label: entry.stockName }
      : null
  }
  onChange={(selected) =>
    handleCategoryFieldChange(
      cat,
      entryIdx,
      "stockName",
      selected?.value || ""
    )
  }
  placeholder={
    cat.category_name === "Mutual Funds"
      ? "Search Mutual Fund..."
      : "Search Stock Name..."
  }
  className="text-[13px]"
  styles={{
    control: (base) => ({
      ...base,
      borderRadius: "8px",
      borderColor: "#D0D5DD",
      minHeight: "40px",
      width: "240px"
    })
  }}
/>
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`unit-${cat.category_name}-${entryIdx}`} className="text-[13px]">Unit</label>
                                                                    <input
                                                                        id={`unit-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Enter unit"
                                                                        value={entry.unit}
                                                                        onChange={e =>
                                                                            handleCategoryFieldChange(cat, entryIdx, "unit", e.target.value)
                                                                        }
                                                                        className="w-[120px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`ta-${cat.category_name}-${entryIdx}`} className="text-[13px]">
                                                                        Total Amount
                                                                    </label>
                                                                    <input
                                                                        id={`ta-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Total amount for this stock"
                                                                        value={entry.totalAmount}
                                                                        onChange={e => handleCategoryFieldChange(cat, entryIdx, "totalAmount", e.target.value)}
                                                                        className={
                                                                            "w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                        }
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`ic-${cat.category_name}-${entryIdx}`} className="text-[13px]">Indirect Charges</label>
                                                                    <input
                                                                        id={`ic-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Indirect charges"
                                                                        value={entry.indirectCharges}
                                                                        onChange={e => handleCategoryFieldChange(cat, entryIdx, "indirectCharges", e.target.value)}
                                                                        className={
                                                                            "w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none"
                                                                        }
                                                                        required
                                                                    />
                                                                </div>
                                                                <div className='flex flex-col'>
                                                                    <label htmlFor={`puc-${cat.category_name}-${entryIdx}`} className="text-[13px]">Per Unit Charge</label>
                                                                    <input
                                                                        id={`puc-${cat.category_name}-${entryIdx}`}
                                                                        type="number"
                                                                        min={0}
                                                                        step="any"
                                                                        placeholder="Per Unit Charge"
                                                                        value={entry.perUnitCharge}
                                                                        onChange={e =>
                                                                            handleCategoryFieldChange(cat, entryIdx, "perUnitCharge", e.target.value)
                                                                        }
                                                                        className={`w-[140px] border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[13px] outline-none ${entry.autoCalculated && !!entry.unit ? "bg-gray-100" : ""}`}
                                                                        readOnly={entry.autoCalculated && !!entry.unit}
                                                                        required
                                                                    />
                                                                    {entry.autoCalculated && !!entry.unit && (
                                                                        <span className="text-xs text-green-500">Auto-calculated</span>
                                                                    )}
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    title="Remove entry"
                                                                    onClick={() => handleRemoveEntry(cat, entryIdx)}
                                                                    className="ml-2 text-red-500 hover:text-red-700 rounded px-2 py-1 border border-red-200 bg-red-50 flex items-center justify-center text-xs"
                                                                    style={{ height: '32px' }}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        )
                                                    })}
                                                    <button
                                                        type="button"
                                                        className="mt-1 text-sm text-blue-500 hover:text-blue-700 font-semibold px-2 py-1 border border-blue-200 rounded bg-blue-50 flex items-center w-fit mb-1"
                                                        onClick={() => handleAddEntry(cat)}
                                                    >
                                                        + Add another for {cat.category_name}
                                                    </button>
                                                    {selectedPartyTotalAmount !== null && entries.length > 0 && (
                                                        <div className="mt-1 text-xs text-blue-700">
                                                            {(() => {
                                                                const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                                let additional = 0;
                                                                Object.entries(categoryEntries).forEach(([category, ents]) => {
                                                                    ents.forEach(ent => {
                                                                        let amt = parseFloat(ent.totalAmount || "0");
                                                                        let indirect = parseFloat(ent.indirectCharges || "0");
                                                                        if (!isNaN(amt)) additional += amt;
                                                                        if (!isNaN(indirect)) additional += indirect;
                                                                    });
                                                                });
                                                                // return `Total used (from stock table): ${alreadyDeducted}, New allocation: ${additional}, of available: ${selectedPartyTotalAmount}`;
                                                                return `New allocation: ${additional}, of available: ${selectedPartyTotalAmount}`;
                                                            })()}
                                                            {(() => {
                                                                const alreadyDeducted = calculateTotalDeductedFromStockTable(stocksDataDb, data.partyId);
                                                                let additional = 0;
                                                                Object.entries(categoryEntries).forEach(([category, ents]) => {
                                                                    ents.forEach(ent => {
                                                                        let amt = parseFloat(ent.totalAmount || "0");
                                                                        let indirect = parseFloat(ent.indirectCharges || "0");
                                                                        if (!isNaN(amt)) additional += amt;
                                                                        if (!isNaN(indirect)) additional += indirect;
                                                                    });
                                                                });
                                                                if (alreadyDeducted + additional > Number(selectedPartyTotalAmount))
                                                                    return (
                                                                        <span className="text-red-500 ml-3">
                                                                            Not enough amount! You are allocating {alreadyDeducted + additional - Number(selectedPartyTotalAmount)} over available amount.
                                                                        </span>
                                                                    );
                                                                return null;
                                                            })()}
                                                            {deductedAvailable !== null && (
                                                                <span className="ml-4" style={{ color: deductedAvailable < 0 ? "#D8000C" : "#007200" }}>
                                                                    Available After Deduction: {deductedAvailable}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    </div>
                    <button
                        type="submit"
                        className="mt-6 flex items-center justify-center gap-2 text-[16px] font-[500] text-[#fff] bg-gradient-to-r from-[#1CAC70] to-[#FF9500] py-2 rounded-[8px] px-8"
                    >
                        Submit
                    </button>
                </form>
            </div>
            {/* ============== Sell Stock Modal ============== */}
            {showSellStockModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md h-full max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
                            <h2 className="text-xl font-semibold text-gray-800">Sell Stock</h2>
                            <button
                                onClick={() => {
                                    setShowSellStockModal(false);
                                    setSellStockForm({
                                        partyId: '',
                                        category: '',
                                        stockId: '',
                                        stockName: '',
                                        sellUnits: '',
                                        sellAmount: '',
                                        indirectCharges: '',
                                        date: '',
                                    });
                                }}
                                className="text-gray-400 hover:text-gray-700 text-2xl"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        {/* Scrollable Form */}
                        <div className="overflow-y-auto flex-1 px-6 py-4">
                            <form
                                id="sell-stock-form-modal"
                                onSubmit={handleSellStockSubmit}
                                autoComplete="off"
                                className="flex flex-col gap-4"
                            >
                                <div>
                                    <label htmlFor="sellstock-partyId" className="block text-sm font-medium text-gray-700 mb-1">
                                        Party Name
                                    </label>
                                    <select
                                        name="partyId"
                                        id="sellstock-partyId"
                                        value={sellStockForm.partyId} 
                                        
                                        onChange={handleSellStockFormChange}
                                        required
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Choose Party</option>
                                        {partiesList.map((p) => (
                                            <option key={p.id} value={String(p.id)}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-category" className="block text-sm font-medium text-gray-700 mb-1">
                                        Category
                                    </label>
                                    <select
                                        name="category"
                                        id="sellstock-category"
                                        value={sellStockForm.category}
                                        onChange={handleSellStockFormChange}
                                        required
                                        disabled={!sellStockForm.partyId}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                    >
                                        <option value="">Select Category</option>
                                        {sellPartyCategories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-stockId" className="block text-sm font-medium text-gray-700 mb-1">
                                        Stock Name
                                    </label>
                                    <select
                                        name="stockId"
                                        id="sellstock-stockId"
                                        value={sellStockForm.stockId}
                                        onChange={handleSellStockFormChange}
                                        required
                                        disabled={!sellStockForm.partyId || !sellStockForm.category}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                    >
                                        <option value="">Select Stock</option>
                                        {sellCategoryStocks.map(stock => (
                                            <option key={stock.id} value={stock.id}>
                                                {stock.stock_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="sellstock-sellUnits" className="block text-sm font-medium text-gray-700 mb-1">
                                        Units To Sell
                                        {sellStockForm.partyId && sellStockForm.category && sellStockForm.stockId ? (
                                            <span className="text-xs text-gray-500 ml-2">
                                                (Available: {sellStockAvailableDetails.unit})
                                            </span>
                                        ) : null}
                                    </label>
                                    <input
                                        type="number"
                                        name="sellUnits"
                                        id="sellstock-sellUnits"
                                        placeholder="How many units to sell"
                                        value={sellStockForm.sellUnits}
                                        min={0}
                                        max={sellStockAvailableDetails.unit || undefined}
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        required
                                        disabled={!sellStockForm.stockId}
                                    />
                                    {sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > sellStockAvailableDetails.unit ? (
                                        <span className="text-xs text-red-500">
                                            Not enough units available!
                                        </span>
                                    ) : null}
                                </div>
                                <div>
                                    <label htmlFor="sellstock-sellAmount" className="block text-sm font-medium text-gray-700 mb-1">
                                        Amount for which you are selling stocks
                                    </label>
                                    <input
                                        type="number"
                                        name="sellAmount"
                                        id="sellstock-sellAmount"
                                        placeholder="Enter sell amount"
                                        value={sellStockForm.sellAmount || ""}
                                        min={0}
                                        step="any"
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        required
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="sellstock-indirectCharges" className="block text-sm font-medium text-gray-700 mb-1">
                                        Indirect Charges
                                    </label>
                                    <input
                                        type="number"
                                        name="indirectCharges"
                                        id="sellstock-indirectCharges"
                                        placeholder="Enter indirect charges (if any)"
                                        value={sellStockForm.indirectCharges || ""}
                                        min={0}
                                        step="any"
                                        onChange={handleSellStockFormChange}
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="sellstock-date" className="block text-sm font-medium text-gray-700 mb-1">
                                        Date
                                    </label>
                                    <input
                                        type="date"
                                        name="date"
                                        id="sellstock-date"
                                        value={sellStockForm.date}
                                        onChange={handleSellStockFormChange}
                                        required
                                        className="w-full border border-gray-300 p-2.5 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
                                        disabled={!sellStockForm.stockId}
                                    />
                                </div>
                                <div className="bg-gray-50 rounded-md p-3 text-sm">
                                    <label className="block font-medium text-gray-700 mb-1">
                                        Sell Amount (auto):
                                        <span className="font-semibold text-gray-800 ml-2">
                                            {sellStockForm.sellAmount && sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > 0
                                                ? (
                                                    <>
                                                        {(parseFloat(sellStockForm.sellAmount) / parseFloat(sellStockForm.sellUnits)).toFixed(2)}
                                                        <span className="text-xs text-gray-500 ml-2">(per unit)</span>
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        {sellStockFormAmount.toFixed(2)}
                                                    </>
                                                )
                                            }
                                        </span>
                                    </label>
                                    <div className="text-xs text-gray-500">
                                        {sellStockForm.sellAmount && sellStockForm.sellUnits && parseFloat(sellStockForm.sellUnits) > 0
                                            ? "Calculated as: selling amount ÷ units to sell"
                                            : "Calculated as: (current_amount of stock ÷ total units) × units to sell"}
                                    </div>
                                    {sellStockForm.indirectCharges && parseFloat(sellStockForm.indirectCharges) > 0 && (
                                        <div className="text-xs text-green-600 mt-1">
                                            Indirect charges will also be recorded for this sale.
                                        </div>
                                    )}
                                </div>
                            </form>
                        </div>
                        {/* Footer buttons, always visible */}
                        <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowSellStockModal(false);
                                    setSellStockForm({
                                        partyId: '',
                                        category: '',
                                        stockId: '',
                                        stockName: '',
                                        sellUnits: '',
                                        sellAmount: '',
                                        indirectCharges: '',
                                        date: '',
                                    });
                                }}
                                className="flex-1 bg-white border border-gray-300 text-gray-800 rounded-md py-2 font-medium hover:bg-gray-100 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="sell-stock-form-modal"
                                disabled={sellStockLoading}
                                className="flex-1 bg-primary-600 text-white rounded-md py-2 font-medium hover:bg-primary-700 transition disabled:opacity-60"
                                style={{ background: "#1CAC70" }}
                            >
                                {sellStockLoading ? "Saving..." : "Sell Stock"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ============ Withdrawal Modal & Form =========== */}
            {showWithdrawalModal && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40">
                    <div className='bg-white rounded-2xl shadow-xl p-6 w-[90vw] max-w-[400px] flex flex-col gap-4'>
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-lg font-semibold text-gray-800">Add Withdrawal</h2>
                            <button
                                onClick={() => {
                                    setShowWithdrawalModal(false);
                                    resetWithdrawalForm();
                                }}
                                className="text-gray-400 hover:text-gray-700 text-xl font-bold"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleWithdrawalSubmit} autoComplete="off">
                            <div className='mb-2'>
                                <label htmlFor="withdrawal-partyId">Party Name</label>
                                <select
                                    name="partyId"
                                    id="withdrawal-partyId"
                                    value={withdrawal.partyId}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                >
                                    <option value="">Choose Party</option>
                                    {partiesList.map((p) => (
                                        <option key={p.id} value={String(p.id)}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className='mb-2'>
                                <label htmlFor="withdrawal-investmentType">Investment</label>
                                <select
                                    name="withdrawalInvestmentType"
                                    id="withdrawal-investmentType"
                                    value={withdrawalInvestmentType}
                                    onChange={handleWithdrawalInput}
                                    required
                                    className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                    disabled={!withdrawal.partyId}
                                >
                                    <option value="direct">Direct Payment</option>
                                    <option value="structure">Structure Payment</option>
                                </select>
                            </div>
                            {withdrawalInvestmentType === 'direct' && (
                                <div className='mb-2'>
                                    <label htmlFor="withdrawal-directPayment">Payment Amount</label>
                                    <input
                                        type="number"
                                        name="withdrawalDirectPayment"
                                        id="withdrawal-directPayment"
                                        placeholder="Enter payment amount"
                                        value={withdrawalDirectPayment}
                                        min={0}
                                        onChange={e => setWithdrawalDirectPayment(e.target.value)}
                                        className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                        required
                                        disabled={!withdrawal.partyId}
                                    />
                                    <div className="mt-2">
                                        <label htmlFor="withdrawal-date-direct">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            id="withdrawal-date-direct"
                                            value={withdrawal.date}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.partyId}
                                        />
                                    </div>
                                </div>
                            )}
                            {withdrawalInvestmentType === 'structure' && (
                                <>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-category">Category</label>
                                        <select
                                            name="category"
                                            id="withdrawal-category"
                                            value={withdrawal.category}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.partyId}
                                        >
                                            <option value="">Choose Category</option>
                                            {withdrawCategoriesDb.map((cat) => (
                                                <option key={cat} value={cat}>{cat}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-stockName">Stock Name</label>
                                        <select
                                            name="stockName"
                                            id="withdrawal-stockName"
                                            value={withdrawal.stockName || ''}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.category}
                                        >
                                            <option value="">Select Stock</option>
                                            {withdrawalStockOptions.map((item) => (
                                                <option key={item.id} value={item.stock_name}>
                                                    {item.stock_name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-amount">
                                            Withdrawal Amount
                                            {withdrawal.partyId && withdrawal.category && withdrawal.stockName && (
                                                <span className="text-xs text-blue-500 ml-2">
                                                    (
                                                    Available unit: {withdrawalUnitLimit}
                                                    )
                                                </span>
                                            )}
                                        </label>
                                        <input
                                            type="number"
                                            name="withdrawalAmount"
                                            id="withdrawal-amount"
                                            placeholder="Withdrawal Amount"
                                            value={withdrawalDirectPayment}
                                        min={0}
                                        onChange={e => setWithdrawalDirectPayment(e.target.value)}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.stockName}
                                        />
                                        
                                    </div>
                                    <div className='mb-2'>
                                        <label htmlFor="withdrawal-date">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            id="withdrawal-date"
                                            value={withdrawal.date}
                                            onChange={handleWithdrawalInput}
                                            required
                                            className='w-full border-[#D0D5DD] border-[1px] p-2 rounded-[8px] text-[#667085] text-[16px] outline-none mt-1'
                                            disabled={!withdrawal.stockName}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="flex gap-2 mt-6">
                                <button
                                    type="submit"
                                    disabled={withdrawalLoading}
                                    className="bg-gradient-to-r from-[#1CAC70] to-[#FF9500] text-white px-6 py-2 rounded-lg font-semibold flex-1 disabled:opacity-60"
                                >
                                    {withdrawalLoading ? "Saving..." : "Submit Withdrawal"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowWithdrawalModal(false);
                                        resetWithdrawalForm();
                                    }}
                                    className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-semibold flex-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

export default Addstock;